import {
  getAfterHoursSettings,
  getCompanies,
  getCompanyMessage,
  updateAfterHoursSettings,
  updateCompanyMessage,
} from '@/services/company-service/company-service';
import {
  UpdateAfterHoursSettings,
  UpdateCompanyMessage,
} from '@/services/company-service/company-service.types';
import { openNotification } from '@/utils/notification/notification';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

export const useCompanyInitialMessage = (companyName: string | undefined) => {
  const { data: companyMessage, isLoading } = useQuery({
    queryKey: ReactQueryKeys.INIT_MESS(companyName),
    queryFn: () => getCompanyMessage(companyName!),
    enabled: !!companyName,
  });

  return {
    companyMessage,
    isLoading,
  };
};

export const useUpdateCompanyMessage = (companyName: string | undefined) => {
  const queryClient = useQueryClient();

  const { mutate: updateCompanyMess, isPending: isPendingUpdate } = useMutation({
    mutationFn: (data: UpdateCompanyMessage) => updateCompanyMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.INIT_MESS(companyName),
      });
    },
  });

  return {
    updateCompanyMess,
    isPendingUpdate,
  };
};

export const useAfterHoursSettings = (companyName: string | undefined) => {
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.AFTER_HOURS_SETTINGS(companyName),
    queryFn: () => getAfterHoursSettings(companyName!),
    enabled: !!companyName,
  });

  return { settings, isLoading, error };
};

export const useUpdateAfterHoursSettings = (companyName: string | undefined) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UpdateAfterHoursSettings) => updateAfterHoursSettings(data),
    onSuccess: (settings) => {
      queryClient.setQueryData(ReactQueryKeys.AFTER_HOURS_SETTINGS(companyName), settings);
      // A new time zone moves the stored business hours and the zone the list reports.
      queryClient.invalidateQueries({ queryKey: ['business-hours'] });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.COMPANIES() });
      openNotification({ message: t('businessHours:afterHours.saved'), type: 'success' });
    },
  });

  return { updateAfterHoursSettings: mutateAsync, isPending };
};

export const useCompaniesList = () => {
  const { data: companies, isPending } = useQuery({
    queryKey: ReactQueryKeys.COMPANIES(),
    queryFn: () => getCompanies(),
  });

  return {
    companies,
    isPending,
  };
};

